// Copyright (c) 2022 by Move Closer

import cloneDeep from 'lodash/cloneDeep'
import { mapModel, MappingConfig } from '@movecloser/front-core'

import { AnyDescription, Description, Identifier, RelatedType, SetDescription } from './data.contracts'
import {
  DriverConfig,
  RelatedRecord,
  RelatedTypeDriver,
  ResolvedTypeReturns,
  ResolvesDriver
} from './service.contracts'

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export abstract class AbstractTypeDriver<D extends Description, R extends Description = D> implements RelatedTypeDriver<D, R> {
  /**
   * Adapter that is used by a driver.
   */
  protected abstract adapterMap: MappingConfig

  /**
   * Types that a driver depends on.
   */
  protected abstract dependencies: RelatedType[]

  /**
   * Key that stores RelatedType.
   */
  protected abstract key: RelatedType

  /**
   * Check if given Description exists at Record.
   */
  public canDescribe (id: string, record: RelatedRecord): boolean {
    if (!this.hasTypeEntries(record)) {
      return false
    }

    return this.hasRelated(id, record)
  }

  /**
   * List of types that driver depends on.
   */
  public dependentTypes (): RelatedType[] {
    return this.dependencies
  }

  /**
   * Return unresolved Description.
   */
  public describe (id: string, record: RelatedRecord): D {
    this.throwWhenMissing(id, record)

    return mapModel<D>(
      { ...record[this.key]?.[`${id}`] },
      this.adapterMap,
      Object.keys(this.adapterMap).length === 0
    )
  }

  /**
   * Return resolved Description.
   */
  public resolve (
    id: string,
    record: RelatedRecord,
    config: DriverConfig,
    driverResolver?: ResolvesDriver
  ): ResolvedTypeReturns<D, R> {
    this.throwWhenMissing(id, record)

    return mapModel<ResolvedTypeReturns<D, R>>(
      this.resolveNested(
        cloneDeep<D>(record[this.key]?.[`${id}`] as D),
        record,
        config,
        driverResolver
      ),
      this.adapterMap,
      Object.keys(this.adapterMap).length === 0
    )
  }

  /**
   * Determine if there's a related in the record.
   */
  protected hasRelated (id: string, record: RelatedRecord): boolean {
    const dictionary = record[this.key]
    if (!dictionary) {
      return false
    }

    return (dictionary[`${id}`] && dictionary[`${id}`] !== null)
  }

  /**
   * Determine of there are entries of given type.
   */
  protected hasTypeEntries (record: RelatedRecord): boolean {
    return !!record[this.key]
  }

  /**
   * Resolve nested types of Description.
   */
  protected resolveNested (
    unresolved: D,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    record: RelatedRecord,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    config: DriverConfig,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    driverResolver?: ResolvesDriver
  ): AnyDescription {
    return unresolved
  }

  /**
   * Throws when related not found.
   */
  protected throwWhenMissing (id: string, record: RelatedRecord): void {
    if (!this.hasTypeEntries(record)) {
      throw new Error(
        `[RelatedType]: There are no entries for the (${this.key}) type in the record!`
      )
    }

    if (!this.hasRelated(id, record)) {
      throw new Error(
        `[RelatedType]: There are no entries for the (${id}) ID & (${this.key}) in the record!`
      )
    }
  }
}

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export abstract class AbstractSetDriver<D extends SetDescription, R extends Description>
  extends AbstractTypeDriver<D, R> {
  /**
   * @inheritDoc
   */
  protected adapterMap: MappingConfig = {}

  /**
   * Type of children element of set.
   */
  protected abstract childType: RelatedType

  /**
   * Current position of list pointer.
   */
  protected positions: Record<string, number> = {}

  /**
   * Determine whether set should be aware of last used item.
   */
  protected shouldTrackLength: boolean = false

  /**
   * Reset positions of Set's type.
   */
  public reset (): void {
    this.positions = {}
  }

  /**
   * @inheritDoc
   */
  public resolve (
    id: string,
    record: RelatedRecord,
    config: DriverConfig,
    driverResolver: ResolvesDriver
  ): ResolvedTypeReturns<D, R> {
    this.throwWhenMissing(id, record)

    const set: SetDescription = this.describe(id, record)

    // Check if the output is as expected.
    if (!Array.isArray(set.items)) {
      throw new Error(
        `[RelatedType]: Failed to resolve items for the (${this.key}) of a given ID (${id})!`
      )
    }

    const childDriver = driverResolver(this.childType)
    const itemsToReduce: Identifier[] = this.composeItemsList(set, config)

    return itemsToReduce.reduce<R[]>((acc: R[], curr: Identifier) => {
      if (!childDriver.canDescribe(`${curr}`, record)) {
        return acc
      }

      return [
        ...acc,
        mapModel<R>(
          childDriver.resolve(`${curr}`, record, config, driverResolver),
          this.adapterMap,
          Object.keys(this.adapterMap).length === 0
        )
      ]
    }, []) as ResolvedTypeReturns<D, R>
  }

  protected composeItemsList (set: SetDescription, config: DriverConfig): Identifier[] {
    if (!this.shouldTrackLength) {
      return set.items
    }

    let contentToFind: Identifier[] = [...set.items]
    // We need to consider an entry conditions. If we have limit set,
    // should we prevent against duplicates & if the pointer should be moved.
    const limit: number = !!config.take && typeof config.take === 'number' ? Math.floor(config.take) : set.totalItems
    const continueOrder: boolean = typeof config.continueOrder === 'undefined' || config.continueOrder
    // In case of limit let's narrow the range.
    const startPosition = continueOrder ? this.getSetPosition(set.id.toString()) : 0
    const endPosition = startPosition + limit

    contentToFind = contentToFind.filter((el: Identifier, index: number) => {
      return startPosition <= index && index < endPosition
    })

    // If end position is bigger than totalItems of Set get next items from the begging
    if (endPosition > set.totalItems) {
      this.whenLengthExceeded(contentToFind, set, endPosition)
    }

    if (continueOrder) {
      this.setPosition(set.id.toString(), endPosition)
    }

    return contentToFind
  }

  protected getSetPosition (set: string): number {
    if (!this.hasSetPosition(set)) {
      this.positions[set] = 0
    }

    return this.positions[set]
  }

  protected hasSetPosition (set: string): boolean {
    return typeof this.positions[set] === 'number'
  }

  protected setPosition (set: string, newPositions: number): void {
    this.positions[set] = newPositions
  }

  /**
   * Carry about scenario when required length is longer than set's items count.
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected whenLengthExceeded (items: Identifier[], set: SetDescription, endPosition: number): void {
    // Note! This method should be use when limit exceeded & there should some action triggered.
  }
}
